import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { LinkEmbedCard } from '../components/LinkEmbedCard';

import { 
  Upload, X, Image as ImageIcon, Video, Plus, Loader2,
  ChevronLeft, ChevronRight, GripVertical, Link2
} from 'lucide-react';


const MAX_IMAGES = 10;
const MAX_VIDEOS = 4;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export default function CreatePostPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragIndex, setDragIndex] = useState(null);
  const fileInputRef = useRef(null);

  // Pre-fill content if shared link passed via navigation state
  useEffect(() => {
    const state = location.state;
    if (state?.sharedLink) {
      const linkText = state.sharedTitle
        ? `${state.sharedTitle}\n${state.sharedLink}`
        : state.sharedLink;
      setContent(linkText);
    }
  }, [location.state]);

  // Auto-detect URLs in content and fetch link preview
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const lastResolvedUrl = useRef('');

  useEffect(() => {
    const urlMatch = content.match(/(https?:\/\/[^\s<]+[^\s<.,;:!?'"\])])/i);
    const detectedUrl = urlMatch ? urlMatch[0] : '';

    if (!detectedUrl) {
      setLinkPreview(null);
      lastResolvedUrl.current = '';
      return;
    }
    if (detectedUrl === lastResolvedUrl.current) return;

    const timer = setTimeout(async () => {
      lastResolvedUrl.current = detectedUrl;
      setLinkLoading(true);
      console.log('[CreatePost] Resolving URL:', detectedUrl);
      try {
        const headers = getAuthHeaders();
        console.log('[CreatePost] Auth headers:', Object.keys(headers));
        const res = await fetch(`${API}/embeds/resolve`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: detectedUrl })
        });
        console.log('[CreatePost] Embed response status:', res.status);
        if (res.ok) {
          const data = await res.json();
          console.log('[CreatePost] Embed data:', data?.title);
          if (data?.title) setLinkPreview(data);
          else setLinkPreview(null);
        }
      } catch (err) {
        console.error('[CreatePost] Embed fetch error:', err);
      }
      setLinkLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [content, getAuthHeaders]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = [];
    const previews = [];
    
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        toast.error(`${file.name}: Unsupported file type`);
        continue;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: File too large (max 50MB)`);
        continue;
      }

      // Check limits
      const currentImages = mediaFiles.filter(f => f.type.startsWith('image/')).length;
      const currentVideos = mediaFiles.filter(f => f.type.startsWith('video/')).length;

      if (isImage && currentImages + validFiles.filter(f => f.type.startsWith('image/')).length >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images allowed`);
        continue;
      }

      if (isVideo && currentVideos + validFiles.filter(f => f.type.startsWith('video/')).length >= MAX_VIDEOS) {
        toast.error(`Maximum ${MAX_VIDEOS} videos allowed`);
        continue;
      }

      validFiles.push(file);
      previews.push({
        file,
        url: URL.createObjectURL(file),
        type: isImage ? 'image' : 'video',
        name: file.name
      });
    }

    setMediaFiles(prev => [...prev, ...validFiles]);
    setMediaPreview(prev => [...prev, ...previews]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeMedia = (index) => {
    URL.revokeObjectURL(mediaPreview[index].url);
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
    setMediaPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    
    // Reorder
    const newMediaFiles = [...mediaFiles];
    const newMediaPreview = [...mediaPreview];
    
    const [draggedFile] = newMediaFiles.splice(dragIndex, 1);
    const [draggedPreview] = newMediaPreview.splice(dragIndex, 1);
    
    newMediaFiles.splice(index, 0, draggedFile);
    newMediaPreview.splice(index, 0, draggedPreview);
    
    setMediaFiles(newMediaFiles);
    setMediaPreview(newMediaPreview);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleSubmit = async () => {
    if (!content.trim() && mediaFiles.length === 0) {
      toast.error('Add some content or media to post');
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('content', content);
      
      mediaFiles.forEach((file, index) => {
        formData.append('media', file);
      });

      const headers = getAuthHeaders();
      delete headers['Content-Type']; // Let browser set multipart boundary

      const res = await fetch(`${API}/posts/create`, {
        method: 'POST',
        headers,
        body: formData
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create post');
      }
      
      // Verify we got a post_id back
      if (!data.post_id) {
        console.error('[CreatePost] No post_id in response:', data);
        throw new Error('Post created but no ID returned');
      }
      
      console.log('[CreatePost] Post created successfully:', data.post_id);
      toast.success('Post created!');
      
      // Navigate to feed with a flag to force refresh
      navigate('/feed?refresh=true');
    } catch (err) {
      console.error('[CreatePost] Error:', err);
      toast.error(err.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="create-post-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl font-bold">Create Post</h1>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <Card className="glass-card border-border p-6">
          {/* Content */}
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            className="min-h-[120px] mb-4 bg-transparent border-none text-lg resize-none focus:ring-0"
            maxLength={2200}
          />

          {/* Character count */}
          <div className="text-xs text-muted-foreground text-right mb-4">
            {content.length}/2200
          </div>

          {/* Link Preview */}
          {linkLoading && (
            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground p-3 rounded-lg border border-border bg-muted/20">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Fetching link preview...</span>
            </div>
          )}
          {linkPreview && !linkLoading && (
            <div className="mb-4 relative" data-testid="compose-link-preview">
              <LinkEmbedCard embed={linkPreview} compact />
              <button
                onClick={() => { setLinkPreview(null); lastResolvedUrl.current = 'dismissed'; }}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/60 flex items-center justify-center hover:bg-background/80 transition-colors z-10"
                title="Remove preview"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Media Preview */}
          {mediaPreview.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">
                  {mediaPreview.length} file{mediaPreview.length > 1 ? 's' : ''} selected
                </span>
                <span className="text-xs text-muted-foreground">Drag to reorder</span>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                {mediaPreview.map((media, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`relative aspect-square rounded-lg overflow-hidden bg-muted/30 group cursor-move ${
                      dragIndex === index ? 'opacity-50' : ''
                    }`}
                  >
                    {media.type === 'video' ? (
                      <video
                        src={media.url}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={media.url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {/* Type indicator */}
                    <div className="absolute top-2 left-2 p-1 rounded bg-background/50">
                      {media.type === 'video' ? (
                        <Video className="w-4 h-4" />
                      ) : (
                        <ImageIcon className="w-4 h-4" />
                      )}
                    </div>

                    {/* Drag handle */}
                    <div className="absolute top-2 right-8 p-1 rounded bg-background/50 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-4 h-4" />
                    </div>
                    
                    {/* Remove button */}
                    <button
                      onClick={() => removeMedia(index)}
                      className="absolute top-2 right-2 p-1 rounded-full bg-background/50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>

                    {/* Order number */}
                    <div className="absolute bottom-2 left-2 w-6 h-6 rounded-full bg-background/50 flex items-center justify-center text-xs">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-secondary/50 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/mov,video/webm"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-2">
              Drag & drop or click to upload
            </p>
            <p className="text-xs text-muted-foreground">
              JPG, PNG, WEBP, MP4, MOV, WEBM (max 50MB)
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Up to {MAX_IMAGES} images and {MAX_VIDEOS} videos
            </p>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Post
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
